/**
 * Keeps Unity's WebGL runtime from reacting to keystrokes typed into DOM fields.
 *
 * Unity registers keydown/keyup/keypress on `document` in the capture phase, so those
 * listeners run before the event reaches React's root container. Hiding a keystroke
 * from Unity by stopping propagation therefore hides it from every React key handler
 * in the app as well. Wrapping the registrations instead makes Unity's listeners no-op
 * while a field has focus, leaving propagation — and React — untouched.
 */

const KEY_EVENT_TYPES = new Set(["keydown", "keyup", "keypress"]);
const CLIPBOARD_EVENT_TYPES = new Set(["copy", "cut", "paste"]);
const GUARDED_TARGETS = [document, window];

const wrappers = new Map();
let installed = false;

function isTypingInDomField() {
  const active = document.activeElement;
  if (!active) return false;
  return active.tagName === "TEXTAREA" || active.tagName === "INPUT" || active.isContentEditable;
}

function isUnityCanvasFocused() {
  return document.activeElement?.tagName === "CANVAS";
}

function shouldSkip(type) {
  if (CLIPBOARD_EVENT_TYPES.has(type)) return !isUnityCanvasFocused();
  return isTypingInDomField();
}

function shouldWrap(type, listener, options) {
  if ((!KEY_EVENT_TYPES.has(type) && !CLIPBOARD_EVENT_TYPES.has(type)) || typeof listener !== "function") return false;
  // A skipped call still consumes a `once` listener, so leave those registered as-is.
  return !(typeof options === "object" && options !== null && options.once);
}

function wrappersFor(type) {
  if (!wrappers.has(type)) wrappers.set(type, new WeakMap());
  return wrappers.get(type);
}

function guardedListener(type, listener) {
  const byListener = wrappersFor(type);
  let wrapper = byListener.get(listener);
  if (!wrapper) {
    wrapper = function (event) {
      if (shouldSkip(type)) return;
      listener.call(this, event);
    };
    byListener.set(listener, wrapper);
  }
  return wrapper;
}

function guardTarget(target) {
  const originalAdd = target.addEventListener;
  const originalRemove = target.removeEventListener;

  target.addEventListener = function (type, listener, options) {
    const effective = shouldWrap(type, listener, options) ? guardedListener(type, listener) : listener;
    return originalAdd.call(this, type, effective, options);
  };

  target.removeEventListener = function (type, listener, options) {
    const effective = shouldWrap(type, listener, options)
      ? wrappersFor(type).get(listener) || listener
      : listener;
    return originalRemove.call(this, type, effective, options);
  };
}

/**
 * Idempotent. The patch is never reverted: Unity's listeners live for the lifetime of
 * the page, and unpatching would only stop wrapping future registrations while leaving
 * the already-wrapped ones in place.
 */
export function installUnityKeyboardGuard() {
  if (installed) return;
  installed = true;
  GUARDED_TARGETS.forEach(guardTarget);
}
